import { useVehicleData } from '@finn/auto-ui/hooks';
import { Skeleton } from '@finn/design-system/atoms/skeleton';
import {
  FilterValuesObject,
  SortKey,
  useProducts,
  useSubscriptionPrice,
  VehicleProvider,
} from '@finn/ua-vehicle';
import { parseToHtml } from '@finn/ui-utils';
import dynamic from 'next/dynamic';
import { useRef } from 'react';

type TechnicalModelDetailsProps = {
  title?: string;
  product_id?: string;
  items?: {
    title: string;
    description: string;
    icon: string;
  }[];
  filters?: FilterValuesObject;
};

const DynamicIcon = dynamic(
  () => import('./DynamicIcon').then((res) => res.DynamicIcon),
  {
    ssr: true,
  }
);

const SubscriptionPrice = () => {
  const { finalPriceFormatted } = useSubscriptionPrice();

  return <>{finalPriceFormatted}</>;
};

const renderItemDescription = (description: string, { vehicle }) => {
  if (description?.includes('{product.price}')) {
    return vehicle ? (
      <SubscriptionPrice />
    ) : (
      <Skeleton className="h-8 w-1/2 rounded-sm" />
    );
  }

  return parseToHtml(description);
};

export const TechnicalModelDetails = ({
  title,
  product_id,
  items,
  filters,
}: TechnicalModelDetailsProps) => {
  const {
    vehicle,
    isLoading: isLoadingById,
    error: errorRaw,
  } = useVehicleData(product_id, null);
  const ref = useRef<HTMLDivElement>(null);
  const error = errorRaw || (vehicle as any)?.error;

  const { products, isLoading: isLoadingAll } = useProducts({
    filters: {
      ...filters,
      sort: SortKey.ASC,
    },
    revalidateOnMount: true,
    limit: 1,
    gridRef: ref,
  });

  const isLoading = isLoadingById || isLoadingAll;
  const vehicleData = vehicle || products?.[0];

  return (
    <VehicleProvider vehicle={vehicleData} fearAwayItems={[]}>
      <div className="mx-auto md:max-w-screen-max">
        <div className="container my-0 ml-auto md:mr-0 md:!w-[calc(100vw-4rem)] md:!max-w-[calc(100vw-4rem)] lg:!w-[calc(50%+630px)] lg:!max-w-[calc(50%+630px)]">
          <h2 className="mb-6 mobile-t4-semibold md:web-t4-semibold">
            {parseToHtml(title)}
          </h2>
          <ul className="m-0 grid grid-cols-2 gap-x-10 gap-y-6 p-0 md:grid-cols-4 md:gap-x-6 md:gap-y-10">
            {items?.map((item, index) => (
              <li key={index} className="flex flex-col gap-4">
                <DynamicIcon iconName={item.icon} />
                <p className="body-16-light">{parseToHtml(item.title)}</p>
                <p className="body-16-semibold">
                  {isLoading && item.description?.includes('{') ? (
                    <Skeleton className="h-8 w-1/2 rounded-sm" />
                  ) : (
                    renderItemDescription(item.description, {
                      vehicle: error ? null : vehicleData,
                    })
                  )}
                </p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </VehicleProvider>
  );
};
